import React, { useState } from "react";
import omit from "lodash/omit";
import URLUtils from "util/URLUtils";
import fetch from "logic/rest/FetchProvider";
import { BootstrapModalForm, Button, Input } from "components/bootstrap";
import { IfPermitted } from "components/common";
import { ConfigurationsActions } from "stores/configurations/ConfigurationsStore";
import { getValueFromInput } from "util/FormsUtils";
import useProductName from "brand-customization/useProductName";
import { PLUGIN_API_ENDPOINT, PLUGIN_CONFIG_CLASS_NAME } from "../Constants";
import UserNotification from "../../util/UserNotification";
const _initialState = (config) => omit(config, ["secret_key", "secret_key_salt"]);
const postConfigUpdate = (update) => {
  const url = URLUtils.qualifyUrl(PLUGIN_API_ENDPOINT);
  return fetch("PUT", url, update);
};
const AWSPluginConfiguration = ({
  config = {
    lookups_enabled: false,
    lookup_regions: "us-east-1,us-west-1,us-west-2,eu-west-1,eu-central-1",
    access_key: "",
    secret_key: "",
    proxy_enabled: false
  }
}) => {
  const productName = useProductName();
  const [updateConfig, setUpdateConfig] = useState(_initialState(config));
  const [showAwsConfigModal, setShowAwsConfigModal] = useState(false);
  const updateConfigField = (field, value) => {
    setUpdateConfig({ ...updateConfig, [field]: value });
  };
  const onFocusSecretKey = () => {
    setUpdateConfig({ ...updateConfig, secret_key: "" });
  };
  const onUpdate = (field) => (value) => {
    if (typeof value === "object") {
      updateConfigField(field, getValueFromInput(value.target));
    } else {
      updateConfigField(field, value);
    }
  };
  const openModal = () => {
    setShowAwsConfigModal(true);
  };
  const closeModal = () => {
    setShowAwsConfigModal(false);
  };
  const resetConfig = () => {
    setUpdateConfig(_initialState(config));
    closeModal();
  };
  const saveConfig = () => {
    postConfigUpdate(updateConfig).then(
      () => {
        ConfigurationsActions.list(PLUGIN_CONFIG_CLASS_NAME);
        closeModal();
      },
      (error) => {
        UserNotification.error(
          `AWS plugin configuration failed with status: ${error}`,
          "Could not save AWS plugin configuration."
        );
      }
    );
  };
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h3", null, "AWS Plugin Configuration"), /* @__PURE__ */ React.createElement("p", null, "Base configuration for all plugins the AWS module is providing. Note that some parameters will be stored in MongoDB without encryption. Users with required permissions will be able to read them in the configuration dialog on this page."), /* @__PURE__ */ React.createElement("dl", { className: "deflist" }, /* @__PURE__ */ React.createElement("dt", null, "Instance detail lookups:"), /* @__PURE__ */ React.createElement("dd", null, config.lookups_enabled === true ? "Enabled" : "Disabled"), /* @__PURE__ */ React.createElement("dt", null, "Connect through proxy:"), /* @__PURE__ */ React.createElement("dd", null, config.proxy_enabled === true ? "Enabled" : "Disabled"), /* @__PURE__ */ React.createElement("dt", null, "Lookup regions:"), /* @__PURE__ */ React.createElement("dd", null, config.lookup_regions ? config.lookup_regions : "[not set]"), /* @__PURE__ */ React.createElement("dt", null, "Access Key:"), /* @__PURE__ */ React.createElement("dd", null, config.access_key ? config.access_key : "[not set]"), /* @__PURE__ */ React.createElement("dt", null, "Secret Key:"), /* @__PURE__ */ React.createElement("dd", null, config.secret_key ? "***********" : "[not set]")), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "clusterconfigentry:edit" }, /* @__PURE__ */ React.createElement(Button, { bsStyle: "info", bsSize: "xs", onClick: openModal }, "Edit configuration")), /* @__PURE__ */ React.createElement(
    BootstrapModalForm,
    {
      show: showAwsConfigModal,
      title: "Update AWS Plugin Configuration",
      onSubmitForm: saveConfig,
      onCancel: resetConfig,
      submitButtonText: "Update configuration"
    },
    /* @__PURE__ */ React.createElement("fieldset", null, /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "aws-lookups-enabled",
        type: "checkbox",
        label: "Run AWS instance detail lookups for IP addresses?",
        help: /* @__PURE__ */ React.createElement("span", null, "When enabled, a message processor will try to identify IP addresses of your AWS entities (like EC2, ELB, RDS, ...) and add additional information abut the service or instance behind it. It can take up to a minute for a change of this to take effect."),
        name: "lookups_enabled",
        checked: updateConfig.lookups_enabled,
        onChange: onUpdate("lookups_enabled")
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "aws-access-key",
        type: "text",
        label: "AWS Access Key",
        help: /* @__PURE__ */ React.createElement("span", null, "Note that this will only be used in encrypted connections but stored in plaintext. Please consult the documentation for suggested rights to assign to the underlying IAM user."),
        name: "access_key",
        value: updateConfig.access_key,
        onChange: onUpdate("access_key")
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "aws-secret-key",
        type: "password",
        label: "AWS Secret Key",
        help: /* @__PURE__ */ React.createElement("span", null, "Note that this will only be used in encrypted connections and will be stored encrypted (using the system secret). Please consult the documentation for suggested rights to assign to the underlying IAM user."),
        name: "secret_key",
        value: updateConfig.secret_key !== void 0 ? updateConfig.secret_key : config.secret_key,
        onFocus: onFocusSecretKey,
        onChange: onUpdate("secret_key")
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "aws-lookup-regions",
        type: "text",
        label: "Lookup regions",
        help: /* @__PURE__ */ React.createElement("span", null, "The AWS instance lookup message processor keeps a table of instances for fast address translation. Define the AWS regions you want to include in the tables. This should be all regions you run AWS services in. Remember that your IAM user needs permission for these regions or you will see warnings in your ", productName, " server logs."),
        name: "lookup_regions",
        value: updateConfig.lookup_regions,
        onChange: onUpdate("lookup_regions")
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "aws-proxy-enabled",
        type: "checkbox",
        label: "Use HTTP proxy?",
        help: /* @__PURE__ */ React.createElement("span", null, "When enabled, we'll access the AWS APIs through the HTTP proxy configured (", /* @__PURE__ */ React.createElement("code", null, "http_proxy_uri"), ") in your ", productName, " configuration file.", /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement("em", null, "Important:"), " You have to restart all AWS inputs for this configuration to take effect."),
        name: "proxy_enabled",
        checked: updateConfig.proxy_enabled,
        onChange: onUpdate("proxy_enabled")
      }
    ))
  ));
};
export default AWSPluginConfiguration;
